/**
 * The brand's website look on the Art Style page: pure helpers behind `useSiteRegen` and `SiteRegenCard`.
 *
 * spark-creatives runs three site jobs, each a `site_regen` creative job, one per brand at a time (specs
 * `.claude/specs/2026-10-06-owner-style-casino-creation.md` and its 2026-10-07 addendum). A job that draws art
 * ("Regenerate website art" and a catalog look's "Generate art in this style") is charged the `site_regen` price once,
 * when it is queued, and refunded only when casino-creation never took the work or cancelled it; a switch, a revert, a
 * catalog look's "Set as primary" and a Retry that resumes a failed job are free:
 * - "Regenerate website art" draws the brand's website art in its active style, then switches the site to it (phase
 *   drawing, then live; when the site already shows that style, the art players see is replaced in place as it is
 *   drawn). A regeneration from before it switched the site ends ready;
 * - "Switch my site" makes the site show art that is ready (switching, then live);
 * - "Back to the original look" puts the original style back (reverting, then original);
 * - a catalog look's "Generate art in this style" draws the site's art in it next to the current look (`look_draw`:
 *   drawing, then ready) and its "Set as primary" switches the site to it (`look_apply`: switching, then live).
 * None of them can be stopped once queued: the page shows no Stop for them. What the site shows is `live_style_id`.
 */
import type { TFunction } from 'i18next';

import type { CreativeJob } from '@/store/slices/creativeJobs';

import {
  SITE_REGEN_KIND,
  isOwnerLookId,
  type ArtStyleFailure,
  type SiteRegenAction,
  type SiteRegenActivity,
  type SiteRegenPhase,
  type SiteRegenState,
} from './art-style-api';
import { ART_STYLE_RATING_NEEDED } from './sample-jobs';

/** A site action the owner can ask for. */
export type SiteAction = Exclude<SiteRegenAction, ''>;

/** How often a running site job is re-read while the page is open. The `creative_job` events are best-effort and a
 *  job runs for hours; spark-creatives reads casino-creation's pipeline every 45 seconds. */
export const SITE_REGEN_POLL_MS = 60_000;

/** The code spark-creatives answers for a style without a definition (it is the message itself). */
const NEEDS_DEFINITION = 'extract or write the style definition first';

const RUNNING_PHASES: readonly SiteRegenPhase[] = ['drawing', 'switching', 'reverting'];
const END_PHASES: readonly SiteRegenPhase[] = ['ready', 'live', 'original'];
const ACTIVITIES: readonly SiteRegenActivity[] = ['preparing', 'submitting', 'waiting', 'following'];

/** A site job is queued or running. Pure. */
export const isRunningPhase = (phase: string | undefined): phase is SiteRegenPhase =>
  RUNNING_PHASES.some((running) => running === phase);

const isEndPhase = (phase: string | undefined): phase is SiteRegenPhase => END_PHASES.some((end) => end === phase);

const activityOf = (value: string | undefined, fallback: SiteRegenActivity): SiteRegenActivity =>
  value === '' ? '' : ACTIVITIES.find((activity) => activity === value) ?? fallback;

/**
 * Whether the brand's site shows its own style, as far as the state tells: `owner` once live; `not_owner` before
 * anything was switched, while the drawn art waits for a switch, or once the original look is back; `unknown` after
 * a failure (a failed regeneration leaves the look as it was, a failed switch or revert can leave it mixed) and while
 * a job runs. A regeneration replaces the art players see in place wherever the site shows the brand's style. Pure.
 */
export type SiteLook = 'owner' | 'not_owner' | 'unknown';

export const siteLookOf = (state: SiteRegenState | null): SiteLook => {
  if (!state || isRunningPhase(state.phase)) return 'unknown';
  // The service names what the site shows (the brand's own look, or a catalog one): trusted over the phase, which
  // only tells what the latest job did (a catalog look's switch also ends live).
  if (state.live_style_id) return isOwnerLookId(state.live_style_id) ? 'owner' : 'not_owner';
  // A service from before the catalog looks: the phase is all there is.
  if (state.phase === 'live' && !isLookAction(state.action)) return 'owner';
  if (state.phase === 'idle' || state.phase === 'ready' || state.phase === 'original') return 'not_owner';
  return 'unknown';
};

/** A site job on one of the brand's catalog looks (drawing its art, or switching the site to it). Pure. */
export const isLookAction = (action: SiteRegenAction): boolean => action === 'look_draw' || action === 'look_apply';

/** The catalog look a running site job works on ("" when none). Pure. */
export const lookInFlight = (state: SiteRegenState | null): string =>
  state && isRunningPhase(state.phase) && isLookAction(state.action) ? state.target_style_id : '';

/**
 * The state with its job's live progress on top. The read state is the record; the job's `creative_job` frames (the
 * websocket, the jobs list) move its phase, step and parts between reads. Once the job has ended, its end shows until
 * the re-read lands (which brings the failed parts and casino-creation's reason). A state that is not running is
 * never moved by a frame (the frame is older). Pure.
 */
export const liveSiteRegen = (state: SiteRegenState, job: CreativeJob | undefined): SiteRegenState => {
  if (!job || job.id !== state.job_id || !isRunningPhase(state.phase)) return state;
  const parts = {
    steps_done: job.completed ?? state.steps_done,
    steps_total: job.total ?? state.steps_total,
  };
  if (job.status === 'queued' || job.status === 'running') {
    return {
      ...state,
      ...parts,
      phase: isRunningPhase(job.stage) ? job.stage : state.phase,
      activity: activityOf(job.activity, state.activity),
    };
  }
  if (job.status === 'done' && isEndPhase(job.stage)) return { ...state, ...parts, phase: job.stage, activity: '' };
  if (job.status === 'failed')
    return { ...state, ...parts, phase: 'failed', activity: '', error: job.error || state.error };
  return state;
};

/** The brand's site jobs in flight other than `knownJobId` (one started in another tab, say). Pure. */
export const otherActiveSiteJobs = (
  jobs: Record<string, CreativeJob>,
  tenantId: number,
  knownJobId: string | undefined,
): string[] =>
  Object.values(jobs)
    .filter(
      (job) =>
        job.kind === SITE_REGEN_KIND &&
        String(job.tenant_id) === String(tenantId) &&
        (job.status === 'queued' || job.status === 'running') &&
        job.id !== knownJobId,
    )
    .map((job) => job.id);

/** A finished job of the state's that the state still shows running (its end needs a re-read). Pure. */
export const endedJobToRead = (state: SiteRegenState | null, job: CreativeJob | undefined): string | null =>
  state &&
  job &&
  job.id === state.job_id &&
  isRunningPhase(state.phase) &&
  (job.status === 'done' || job.status === 'failed')
    ? job.id
    : null;

/**
 * The brand's site in its own style before the switch: `base` (the site's address) with the state's `preview_param`
 * (`?site_art_style_banner_set_id=owner-<tenant>`) merged into its query. "" without either, or for a base that is
 * not http(s). Pure.
 */
export const sitePreviewUrl = (base: string, previewParam: string): string => {
  if (!base || !previewParam) return '';
  let url: URL;
  try {
    url = new URL(base);
  } catch {
    return '';
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return '';
  new URLSearchParams(previewParam.replace(/^\?/, '')).forEach((value, key) => url.searchParams.set(key, value));
  return url.toString();
};

/* ── copy ───────────────────────────────────────────────────────────────────── */

const DEPLOYMENT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** `WEEKLY_CASHBACK` → "Weekly cashback". Pure. */
const humanize = (raw: string): string => {
  const words = raw
    .replace(/[_:\s-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
};

/** A casino-creation evergreen's name as a preview card labels it: a code name is humanised (`WEEKLY_CASHBACK` →
 *  "Weekly cashback"); a name already written for people keeps its own casing (`AirTimer`, `Drops&Wins`), with
 *  underscores read as spaces. "" for none. Pure. */
export const evergreenLabel = (raw: string): string => {
  if (!/[a-z]/.test(raw)) return humanize(raw);
  return raw.replace(/_+/g, ' ').replace(/\s+/g, ' ').trim();
};

/** One failed part as the owner reads it. Pure (given `t`). */
export const partLabel = (part: string, t: TFunction): string => {
  if (part === 'welcome_bonus') return t('Welcome bonus');
  if (part === 'above_the_fold_hero') return t('Hero');
  if (part === 'system_art') return t('Shared prize, challenge and shop icons');
  if (part === 'switch') return t('Switching your site');
  const [kind, ...rest] = part.split(':');
  const name = humanize(rest.join(' '));
  if (kind === 'evergreen' && name) return t('Promotion banner: {{name}}', { name });
  if (kind === 'gamification' && name) return t('Gamification art: {{name}}', { name });
  return humanize(part);
};

/** The failed parts as the card lists them: each named once; the content casino-creation names by id (art missing
 *  for a switch or a revert) is counted, not listed. Pure (given `t`). */
export const failedPartLabels = (parts: readonly string[], t: TFunction): string[] => {
  const named = parts.filter((part) => !DEPLOYMENT_ID.test(part));
  const ids = parts.length - named.length;
  const labels = Array.from(new Set(named.map((part) => partLabel(part, t))));
  if (ids > 0) labels.push(t('Content items: {{n}}', { n: ids }));
  return labels;
};

/** Why a site action could not start (its request's refusal). Pure (given `t`). */
export const siteRefusalCopy = (failure: ArtStyleFailure, action: SiteAction, t: TFunction): string => {
  switch (failure.message) {
    case 'site_regen_unavailable':
      return t("Your website art can't be changed here right now.");
    case 'site_regen_running':
      return t('Your website art is already being changed. You can follow it on this page.');
    case 'site_regen_busy':
      return t('Your site is busy with other changes. Please try again in a few minutes.');
    case 'site_regen_nothing_to_switch':
      return t('There is no finished website art to switch to yet. Regenerate your website art first.');
    case 'site_regen_no_original':
      return t("Your site's original look isn't on record, so it can't be put back.");
    case 'site_regen_not_live':
      return t('Your site already shows its original look.');
    case 'art_style_not_active':
      return t('Set this style as primary first.');
    case 'site_look_unknown':
      return t("This look isn't available for your site.");
    case 'site_regen_missing_art':
      return t('Some of your content has no art in this look yet. Generate art in this style first.');
    case NEEDS_DEFINITION:
      return t('Extract a style first.');
    case ART_STYLE_RATING_NEEDED:
      return t('Lumio needs to rate this style before it can update your website. Extract it again to rate it.');
    default:
      break;
  }
  if (action === 'switch' || action === 'look_apply') return t("Your site couldn't be switched. Please try again.");
  if (action === 'look_draw') return t("The art in this style couldn't be started. Please try again.");
  if (action === 'revert') return t("Your original look couldn't be put back. Please try again.");
  return t("Your website art couldn't be started. Please try again.");
};

/** What a running site job is doing right now. Pure (given `t`). */
export const siteActivityCopy = (state: SiteRegenState, t: TFunction): string => {
  if (state.activity === 'preparing') return t('Preparing your style…');
  if (state.activity === 'submitting') return t('Starting…');
  if (state.activity === 'waiting') return t('Waiting for your site to finish other changes…');
  if (state.activity === 'following' && state.steps_total > 0) {
    return t('{{done}} of {{total}} parts done', { done: state.steps_done, total: state.steps_total });
  }
  if (state.activity === 'following') return t('Working on it…');
  return t('Queued…');
};

/** The headline of a failed site job. Pure (given `t`). */
export const siteFailureTitle = (action: SiteRegenAction, t: TFunction): string => {
  if (action === 'switch' || action === 'look_apply') return t("Your site couldn't be switched");
  if (action === 'look_draw') return t("The art in this style couldn't be finished");
  if (action === 'revert') return t("Your original look couldn't be put back");
  return t("Your website art couldn't be finished");
};

/** What went wrong in a failed site job, from its code. Pure (given `t`). */
export const siteFailureCopy = (state: SiteRegenState, t: TFunction): string => {
  switch (state.error) {
    case 'site_regen_busy':
      return t('Your site was busy with other changes for too long.');
    case 'site_regen_no_targets':
      return t('Your site has no art to redraw yet.');
    case 'site_regen_style_invalid':
      return t("Your art style couldn't be used for your website.");
    case 'site_regen_stalled':
      return t('It stopped making progress, so it was ended.');
    case 'site_regen_missing_art':
      if (state.action === 'look_apply') {
        return t('Some of your content has no art in this look yet. Generate art in this style first.');
      }
      return state.action === 'revert'
        ? t('Some of your content only has art in your own style, so your site cannot go back yet.')
        : t('Some of your content has no art in your style yet. Regenerate your website art to switch your site.');
    case 'site_look_unknown':
      return t("This look isn't available for your site.");
    case 'site_regen_unavailable':
      return t("Your website couldn't be reached.");
    case 'art_style_site_sync_failed':
      return t("Your art style couldn't be sent to your website.");
    case 'art_style_describe_failed':
      return t("Lumio couldn't describe your characters.");
    case 'art_style_not_active':
      return t('Your style is no longer primary.');
    case NEEDS_DEFINITION:
      return t('Extract a style first.');
    case 'cancelled_by_owner':
      return t('It was stopped.');
    default:
      return state.failed_parts.length > 0
        ? t("Some parts couldn't be finished.")
        : t('Something went wrong on our side.');
  }
};
